import { useEffect, useRef } from "react";
import { trpc } from "./trpc";
import { env } from "./env";
import { emVerComo } from "./ver-como";

/**
 * Assina o stream SSE de notificações em tempo real. Quando recebe evento
 * `notification`, invalida os caches React Query relevantes (unreadCount +
 * list), o que re-renderiza o sino imediatamente.
 *
 * O EventSource do browser reconecta automaticamente em perdas de rede, e o
 * servidor manda heartbeat a cada 25s pra manter conexão viva.
 *
 * Em dev o Vite proxy redireciona /api/notifications/stream pra :3003.
 */
/** Notificações que significam "a rede de parceiros mudou" — o sino não é o único cache afetado. */
const TIPOS_DE_CONEXAO = new Set([
  "PARTNER_CONNECTED",
  "PROSTHESIS_LAB_CONNECTED",
  "PARTNER_INVITE_ACCEPTED",
]);

export function useNotificationStream(options?: { onPush?: () => void }) {
  const utils = trpc.useUtils();
  const onPushRef = useRef(options?.onPush);
  onPushRef.current = options?.onPush;

  useEffect(() => {
    if (typeof window === "undefined" || emVerComo()) return;

    const url = `${env.apiUrl || ""}/api/notifications/stream`;
    const source = new EventSource(url, { withCredentials: true });

    const handleNotification = (event: MessageEvent) => {
      // Re-fetch dos dois queries do sino — barato e garantido correto.
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();

      // Conexão aceita entra por aqui e muda a lista de parceiros: sem isto, quem está com o
      // diálogo de novo pedido aberto continua vendo a lista de antes até recarregar a página —
      // e recarregar joga fora o que já foi preenchido.
      let tipo: string | undefined;
      try {
        tipo = JSON.parse(event.data)?.type;
      } catch {
        // payload sem JSON válido: segue valendo pro sino, só não dá pra decidir o resto
      }
      if (tipo && TIPOS_DE_CONEXAO.has(tipo)) {
        utils.prosthesis.lab.options.invalidate();
        utils.prosthesis.lab.list.invalidate();
        utils.partner.list.invalidate();
        utils.partner.countByType.invalidate();
      }

      onPushRef.current?.();
    };

    source.addEventListener("notification", handleNotification);
    source.addEventListener("ready", () => {
      // noop — só confirma conexão aberta
    });

    source.onerror = () => {
      // EventSource tenta reconectar sozinho. Em 401 ou erro fatal, o
      // readyState fica em CLOSED e não tentamos de novo.
      if (source.readyState === EventSource.CLOSED) {
        // desliga o loop; user precisa re-login
      }
    };

    return () => {
      source.removeEventListener("notification", handleNotification);
      source.close();
    };
  }, [utils]);
}
